import { Minus, Plus } from 'lucide-react';
import { calculateInventoryHireTotal } from '@court-sync/shared';
import { Badge, Button } from '@/components/ui';
import type { PublicInventoryAddon } from '@/services/courtHire';
import { formatCurrency, type Currency } from '@/utils/currency';

export interface SelectedInventoryAddon {
  itemId: string;
  quantity: number;
}

interface CourtHireEquipmentAddonsProps {
  clubSlug: string;
  startAt: Date | null;
  endAt: Date | null;
  selected: SelectedInventoryAddon[];
  onChange: (addons: SelectedInventoryAddon[]) => void;
  availableAddons: PublicInventoryAddon[];
  loadingAddons?: boolean;
}

function formatCents(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency as Currency);
}

export function CourtHireEquipmentAddons({
  startAt,
  endAt,
  selected,
  onChange,
  availableAddons,
  loadingAddons = false,
}: CourtHireEquipmentAddonsProps) {
  const getQuantity = (itemId: string) => selected.find((s) => s.itemId === itemId)?.quantity ?? 0;

  const setQuantity = (item: PublicInventoryAddon, quantity: number) => {
    const next = selected.filter((s) => s.itemId !== item.id);
    if (quantity > 0) {
      next.push({ itemId: item.id, quantity });
    }
    onChange(next);
  };

  if (!startAt || !endAt) {
    return (
      <p className="text-sm text-muted-foreground">
        Select a court and time to see available equipment.
      </p>
    );
  }

  if (loadingAddons) {
    return <p className="text-sm text-muted-foreground">Loading equipment…</p>;
  }

  if (availableAddons.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No hireable equipment is available for this time slot.
      </p>
    );
  }

  return (
    <ul className="space-y-3">
      {availableAddons.map((item) => {
        const qty = getQuantity(item.id);
        const lineTotal =
          qty > 0 && startAt && endAt
            ? calculateInventoryHireTotal(item.hirePriceCents, qty, startAt, endAt)
            : 0;
        return (
          <li
            key={item.id}
            className="flex flex-col gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-foreground">{item.name}</p>
                {item.category ? (
                  <Badge variant="secondary" className="text-xs">
                    {item.category}
                  </Badge>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {formatCents(item.hirePriceCents, item.currency)}/hr ·{' '}
                {item.availableQuantity} available
                {qty > 0 ? ` · ${formatCents(lineTotal, item.currency)} selected` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={qty <= 0}
                onClick={() => setQuantity(item, qty - 1)}
                aria-label={`Decrease ${item.name}`}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-6 text-center text-sm font-medium">{qty}</span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={qty >= item.availableQuantity}
                onClick={() => setQuantity(item, qty + 1)}
                aria-label={`Increase ${item.name}`}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function sumSelectedAddonCents(
  addons: PublicInventoryAddon[],
  selected: SelectedInventoryAddon[],
  startAt: Date,
  endAt: Date
): number {
  const addonMap = new Map(addons.map((a) => [a.id, a]));
  return selected.reduce((sum, entry) => {
    const item = addonMap.get(entry.itemId);
    if (!item) return sum;
    return sum + calculateInventoryHireTotal(item.hirePriceCents, entry.quantity, startAt, endAt);
  }, 0);
}
